import { ImportMeetingsDialog } from "@/components/library/ImportMeetingsDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useActiveDocument } from "@/context/active-document";
import { useDocumentFileAction } from "@/action/use-document-file-action";
import { useArtifactUuid } from "@/hooks/use-artifact-uuid";
import { useLibraryFilesData } from "@/data/use-library-files-data";
import { smartDate } from "@/lib/date";
import { AllowedMimeType, File as FileDTO } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowDown,
  faPaperclip,
  faSpinnerThird,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useState } from "react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { getFileIconByName } from "./FileChip";

type LibraryModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupUuid: string;
  kitUuid: string;
  /**
   * Callback when an existing library file is selected.
   */
  onSelect?: (file: {
    uuid: string;
    name: string;
    mimeType: AllowedMimeType;
  }) => void | Promise<void>;
  /**
   * Callback when a file is chosen to upload but no kitUuid is selected
   * yet (e.g. KitStarter, TemplatesPage).
   * The raw File is held in state by the caller until a kitUuid is selected.
   */
  onFileToUploadSelect?: (file: File) => void;
};

export const LibraryModal = ({
  open,
  onOpenChange,
  groupUuid,
  kitUuid,
  onSelect,
  onFileToUploadSelect,
}: LibraryModalProps) => {
  const { libraryFiles, isLoading } = useLibraryFilesData(
    groupUuid,
    kitUuid || null,
  );
  const { activeEditor } = useActiveDocument();
  const documentUuid = useArtifactUuid("documentUuid");
  const { attachFile } = useDocumentFileAction();
  const hasDocument = !!activeEditor;
  const [importMeetingsOpen, setImportMeetingsOpen] = useState(false);

  const isPickerMode = !!(onSelect || onFileToUploadSelect);

  const handleAttachToDocument = async (uuid: string, name: string) => {
    if (!documentUuid) return;
    try {
      await attachFile(documentUuid, uuid);
    } catch (error) {
      console.error("Failed to attach image:", error);
      toast.error("Could not attach image to document");
      return;
    }
    activeEditor
      ?.chain()
      .focus()
      .setLibraryImage({ src: uuid, alt: name })
      .run();
    toast.success("Image attached to document");
  };

  const handleLibrarySelect = async (file: {
    uuid: string;
    name: string;
    mimeType: AllowedMimeType;
  }) => {
    await onSelect?.(file);
    onOpenChange(false);
  };

  const isImage = (file: FileDTO) => {
    return (
      file.name.toLowerCase().endsWith(".jpg") ||
      file.name.toLowerCase().endsWith(".jpeg") ||
      file.name.toLowerCase().endsWith(".png") ||
      file.name.toLowerCase().endsWith(".gif") ||
      file.name.toLowerCase().endsWith(".webp")
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-300 min-w-0 overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Library</DialogTitle>
            {kitUuid && !isPickerMode ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImportMeetingsOpen(true)}
              >
                <FontAwesomeIcon icon={faCloudArrowDown} className="mr-2" />
                Import from connector
              </Button>
            ) : null}
          </DialogHeader>
          {isLoading ? (
            <div className="flex flex-row items-center justify-center gap-2 p-20">
              <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin" />
              <div className="text-sm font-medium">
                Loading library files...
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">File</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Date Created</TableHead>
                  <TableHead className="w-10">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {libraryFiles.map((file) => (
                  <TableRow key={file.uuid}>
                    <TableCell>
                      {isImage(file) && file.previewUrl ? (
                        <img
                          src={file.previewUrl}
                          alt={file.name}
                          className="h-10 w-10 object-cover"
                        />
                      ) : (
                        <div className="bg-background-4 text-foreground-5 flex h-10 w-10 items-center justify-center">
                          <FontAwesomeIcon
                            icon={getFileIconByName(file.name)}
                            size="lg"
                          />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{file.name}</TableCell>
                    <TableCell>{file.source}</TableCell>
                    <TableCell>{smartDate(file.dateCreated)}</TableCell>
                    <TableCell>
                      {!isPickerMode && hasDocument && isImage(file) && (
                        <Button
                          variant="ghost"
                          onClick={() =>
                            handleAttachToDocument(file.uuid, file.name)
                          }
                        >
                          <FontAwesomeIcon icon={faPaperclip} />
                        </Button>
                      )}
                      {isPickerMode && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleLibrarySelect(file)}
                        >
                          <FontAwesomeIcon icon={faPaperclip} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {kitUuid ? (
        <ImportMeetingsDialog
          open={importMeetingsOpen}
          onOpenChange={setImportMeetingsOpen}
          groupUuid={groupUuid}
          kitUuid={kitUuid}
        />
      ) : null}
    </>
  );
};
